import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { TrashIcon, ArrowTopRightOnSquareIcon, BookOpenIcon, ArrowDownTrayIcon, ChevronDownIcon, ChevronRightIcon, DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { Menu, Transition } from '@headlessui/react'
import { deleteSavedArticle, getArticlePdfDownloadUrl } from '../../Requests/ArticleBibliographyRequests'
import SuccessPopup from '../PopupNotifications/SuccessPopup'
import ErrorPopup from '../PopupNotifications/ErrorPopup'
import DeleteArticleModal from './DeleteArticleModal'
import AttachPdfModal from './AttachPdfModal'
import { sanitizeUserInput } from '../../utils/security'
import { exportBibliographyToWord, EXPORT_FORMATS } from '../../utils/exportBibliography'

function SavedArticlesTable({ articles, loading, pagination, onPageChange, onArticleDeleted, onArticleUpdated }) {
    const { t } = useTranslation()
    const [deletingArticle, setDeletingArticle] = useState(null)
    const [successMessage, setSuccessMessage] = useState('')
    const [errorMessage, setErrorMessage] = useState('')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [articleToDelete, setArticleToDelete] = useState(null)
    const [isExporting, setIsExporting] = useState(false)
    const [expandedAbstracts, setExpandedAbstracts] = useState(new Set())
    const [showAttachPdfModal, setShowAttachPdfModal] = useState(false)
    const [articleForPdf, setArticleForPdf] = useState(null)
    const [downloadingPdf, setDownloadingPdf] = useState(null)

    const handleDeleteClick = (article) => {
        setArticleToDelete(article)
        setShowDeleteModal(true)
    }

    const handleAttachPdfClick = (article) => {
        setArticleForPdf(article)
        setShowAttachPdfModal(true)
    }

    const handleAttachPdfDone = () => {
        if (onArticleUpdated) onArticleUpdated()
    }

    const handleDownloadPdf = async (article) => {
        try {
            setDownloadingPdf(article.id)
            const response = await getArticlePdfDownloadUrl(article.id)
            window.open(response.data.url, '_blank', 'noopener,noreferrer')
        } catch (error) {
            console.error('Error downloading PDF:', error)
            setErrorMessage(t('articleBibliography.errors.downloadFailed'))
        } finally {
            setDownloadingPdf(null)
        }
    }

    const isPdfProcessing = (article) =>
        article.metadata?.processing_status === 'processing'

    const isPdfProcessed = (article) =>
        article.source === 'upload' && article.metadata?.processing_status === 'processed'

    const handleDeleteConfirm = async (articleId, articleTitle) => {
        try {
            setDeletingArticle(articleId)
            await deleteSavedArticle(articleId)
            setSuccessMessage(t('articleBibliography.savedArticles.deleteSuccess'))
            setShowDeleteModal(false)
            setArticleToDelete(null)
            onArticleDeleted()
        } catch (error) {
            console.error('Error deleting article:', error)
            setErrorMessage(t('articleBibliography.errors.deleteFailed'))
        } finally {
            setDeletingArticle(null)
        }
    }

    const openArticle = (url) => {
        if (url) {
            window.open(sanitizeUserInput(url), '_blank', 'noopener,noreferrer')
        }
    }

    const handleExport = async (format) => {
        if (!articles || articles.length === 0) {
            setErrorMessage(t('articleBibliography.export.noArticles'))
            return
        }

        try {
            setIsExporting(true)
            await exportBibliographyToWord(articles, format, 'Bibliography')
            setSuccessMessage(t('articleBibliography.export.success', { format, count: articles.length }))
        } catch (error) {
            console.error('Export failed:', error)
            setErrorMessage(t('articleBibliography.export.failed'))
        } finally {
            setIsExporting(false)
        }
    }

    const toggleAbstract = (articleId) => {
        setExpandedAbstracts(prev => {
            const newSet = new Set(prev)
            if (newSet.has(articleId)) {
                newSet.delete(articleId)
            } else {
                newSet.add(articleId)
            }
            return newSet
        })
    }

    const renderPagination = () => {
        if (!pagination || pagination.last_page <= 1) return null

        const pages = []
        const currentPage = pagination.current_page
        const lastPage = pagination.last_page

        for (let i = Math.max(1, currentPage - 2); i <= Math.min(lastPage, currentPage + 2); i++) {
            pages.push(i)
        }

        return (
            <div className="flex items-center justify-between px-4 py-3 sm:px-6">
                <div className="flex flex-1 justify-between sm:hidden">
                    <button
                        onClick={() => onPageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {t('common.previous')}
                    </button>
                    <button
                        onClick={() => onPageChange(currentPage + 1)}
                        disabled={currentPage === lastPage}
                        className="relative ml-3 inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        {t('common.next')}
                    </button>
                </div>
                <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm text-gray-700">
                            {t('common.pagination.showing')} <span className="font-medium">{((currentPage - 1) * pagination.per_page) + 1}</span> {t('common.pagination.to')} <span className="font-medium">{Math.min(currentPage * pagination.per_page, pagination.total)}</span> {t('common.pagination.of')} <span className="font-medium">{pagination.total}</span> {t('common.pagination.results')}
                        </p>
                    </div>
                    <div>
                        <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm">
                            <button
                                onClick={() => onPageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                            >
                                <span className="sr-only">{t('common.previous')}</span>
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
                                </svg>
                            </button>
                            {pages.map((page) => (
                                <button
                                    key={page}
                                    onClick={() => onPageChange(page)}
                                    className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold ${
                                        page === currentPage
                                            ? 'bg-black text-white focus-visible:outline-offset-2'
                                            : 'text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                            <button
                                onClick={() => onPageChange(currentPage + 1)}
                                disabled={currentPage === lastPage}
                                className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                            >
                                <span className="sr-only">{t('common.next')}</span>
                                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                                </svg>
                            </button>
                        </nav>
                    </div>
                </div>
            </div>
        )
    }

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                <span className="ml-2 text-gray-600">{t('common.loading')}</span>
            </div>
        )
    }

    if (!articles || articles.length === 0) {
        return (
            <div className="text-center py-12">
                <BookOpenIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-semibold text-gray-900">{t('articleBibliography.savedArticles.empty')}</h3>
                <p className="mt-1 text-sm text-gray-500">{t('articleBibliography.savedArticles.emptyDescription')}</p>
            </div>
        )
    }

    return (
        <>
            {/* Export Button */}
            {articles && articles.length > 0 && (
                <div className="mb-4 flex justify-end">
                    <Menu as="div" className="relative">
                        <Menu.Button className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm disabled:opacity-50" disabled={isExporting}>
                            {isExporting ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            ) : (
                                <ArrowDownTrayIcon className="w-4 h-4" />
                            )}
                            {isExporting ? t('articleBibliography.export.exporting') : t('articleBibliography.export.exportButton')}
                            <ChevronDownIcon className="w-4 h-4" />
                        </Menu.Button>
                        <Transition
                            enter="transition ease-out duration-100"
                            enterFrom="transform opacity-0 scale-95"
                            enterTo="transform opacity-100 scale-100"
                            leave="transition ease-in duration-75"
                            leaveFrom="transform opacity-100 scale-100"
                            leaveTo="transform opacity-0 scale-95"
                        >
                            <Menu.Items className="absolute right-0 mt-2 w-48 origin-top-right bg-white border border-gray-200 divide-y divide-gray-100 rounded-md shadow-lg focus:outline-none z-50">
                                {EXPORT_FORMATS.map((format) => (
                                    <Menu.Item key={format.value} as="div">
                                        {({ active }) => (
                                            <button
                                                onClick={() => handleExport(format.value)}
                                                className={`block w-full text-left px-4 py-2 text-sm ${
                                                    active ? 'bg-gray-100' : ''
                                                } hover:bg-gray-100`}
                                                disabled={isExporting}
                                            >
                                                {format.label}
                                            </button>
                                        )}
                                    </Menu.Item>
                                ))}
                            </Menu.Items>
                        </Transition>
                    </Menu>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('articleBibliography.savedArticles.table.title')}
                            </th>
                            <th scope="col" className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('articleBibliography.savedArticles.table.authors')}
                            </th>
                            <th scope="col" className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('articleBibliography.savedArticles.table.journal')}
                            </th>
                            <th scope="col" className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('articleBibliography.savedArticles.table.year')}
                            </th>
                            <th scope="col" className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('articleBibliography.savedArticles.table.citations')}
                            </th>
                            <th scope="col" className="relative px-6 py-3">
                                <span className="sr-only">{t('common.actions')}</span>
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {articles.map((article) => (
                            <React.Fragment key={article.id}>
                                <tr className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {sanitizeUserInput(article.title)}
                                        </div>
                                        {article.abstract && (
                                            <div className="text-sm text-gray-500 line-clamp-2 mt-1">
                                                {sanitizeUserInput(article.abstract.substring(0, 150))}...
                                            </div>
                                        )}
                                        {isPdfProcessing(article) && (
                                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                <span className="animate-pulse">●</span>
                                                {t('articleBibliography.pdfImport.statusBadge.processing')}
                                            </span>
                                        )}
                                        {isPdfProcessed(article) && (
                                            <span className="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                ● {t('articleBibliography.pdfImport.statusBadge.processed')}
                                            </span>
                                        )}
                                        {article.abstract && (
                                            <button
                                                onClick={() => toggleAbstract(article.id)}
                                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 mt-2 font-medium"
                                            >
                                                {expandedAbstracts.has(article.id) ? (
                                                    <>
                                                        <ChevronDownIcon className="w-3 h-3 transform rotate-0" />
                                                        {t('articleBibliography.savedArticles.hideAbstract')}
                                                    </>
                                                ) : (
                                                    <>
                                                        <ChevronRightIcon className="w-3 h-3" />
                                                        {t('articleBibliography.savedArticles.viewFullAbstract')}
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </td>
                                <td className="hidden md:table-cell px-6 py-4 text-sm text-gray-500">
                                    {article.authors && article.authors.length > 0 ? (
                                        <div className="line-clamp-2">
                                            {article.authors.slice(0, 3).map(author => sanitizeUserInput(author.name)).join(', ')}
                                            {article.authors.length > 3 && ' et al.'}
                                        </div>
                                    ) : (
                                        <span className="text-gray-400">{t('common.unknown')}</span>
                                    )}
                                </td>
                                <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-500">
                                    {article.journal_name ? sanitizeUserInput(article.journal_name) : (
                                        <span className="text-gray-400">{t('common.unknown')}</span>
                                    )}
                                </td>
                                <td className="hidden sm:table-cell px-6 py-4 text-sm text-gray-500">
                                    {article.publication_year || (
                                        <span className="text-gray-400">{t('common.unknown')}</span>
                                    )}
                                </td>
                                <td className="hidden lg:table-cell px-6 py-4 text-sm text-gray-500">
                                    {article.cited_by_count !== null ? article.cited_by_count.toLocaleString() : (
                                        <span className="text-gray-400">{t('common.unknown')}</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 text-right text-sm font-medium">
                                    <div className="flex justify-end space-x-2">
                                        {(article.landing_page_url || article.url) && (
                                            <button
                                                onClick={() => openArticle(article.landing_page_url || article.url)}
                                                className="text-black hover:text-gray-600 p-1"
                                                title={t('articleBibliography.savedArticles.openArticle')}
                                            >
                                                <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                                            </button>
                                        )}
                                        {article.file_path && (
                                            <button
                                                onClick={() => handleDownloadPdf(article)}
                                                disabled={downloadingPdf === article.id}
                                                className="text-gray-500 hover:text-black p-1 disabled:opacity-40"
                                                title={t('articleBibliography.downloadPdf.button')}
                                            >
                                                {downloadingPdf === article.id ? (
                                                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-500"></div>
                                                ) : (
                                                    <ArrowDownTrayIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleAttachPdfClick(article)}
                                            disabled={isPdfProcessing(article)}
                                            className="text-gray-500 hover:text-black p-1 disabled:opacity-40"
                                            title={t('articleBibliography.attachPdf.button')}
                                        >
                                            <DocumentArrowUpIcon className="h-5 w-5" />
                                        </button>
                                        <button
                                            onClick={() => handleDeleteClick(article)}
                                            disabled={deletingArticle === article.id}
                                            className="text-red-600 hover:text-red-800 p-1 disabled:opacity-50"
                                            title={t('articleBibliography.savedArticles.delete')}
                                        >
                                            {deletingArticle === article.id ? (
                                                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                                            ) : (
                                                <TrashIcon className="h-5 w-5" />
                                            )}
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            {expandedAbstracts.has(article.id) && article.abstract && (
                                <tr className="bg-gray-50">
                                    <td colSpan="6" className="px-6 py-4">
                                        <div className="text-sm text-gray-700">
                                            <div className="font-medium text-gray-900 mb-2">
                                                {t('articleBibliography.savedArticles.fullAbstract')}
                                            </div>
                                            <p className="leading-relaxed">
                                                {sanitizeUserInput(article.abstract)}
                                            </p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {renderPagination()}

            {successMessage && (
                <SuccessPopup
                    text={successMessage}
                    handleClose={() => setSuccessMessage('')}
                />
            )}

            {errorMessage && (
                <ErrorPopup
                    text={errorMessage}
                    handleClose={() => setErrorMessage('')}
                />
            )}

            {/* Delete Confirmation Modal */}
            <DeleteArticleModal
                open={showDeleteModal}
                setOpen={setShowDeleteModal}
                article={articleToDelete}
                onConfirm={handleDeleteConfirm}
                loading={deletingArticle === articleToDelete?.id}
            />

            {/* Attach PDF Modal */}
            <AttachPdfModal
                open={showAttachPdfModal}
                setOpen={setShowAttachPdfModal}
                article={articleForPdf}
                onDone={handleAttachPdfDone}
            />
        </>
    )
}

export default SavedArticlesTable